'use client';

import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
} from 'react';
import { SField, type SFieldSize, type SFieldStatus } from '../SField';
import { SChip } from '../SChip';
import { STextLink } from '../STextLink';
import { SIcon, type SIconName } from '../SIcon';
import { type SColor } from '../../lib/color';
import { SPortal } from '../SPortal';
import { cn } from '../../lib/cn';
import { AUTOFILL_RESET_CLASS } from '../../lib/autofill';
import { runRules, useFormField, type Rule } from '../../lib/form';
import { type STooltipProps } from '../STooltip';

export type SChipInputSize = SFieldSize;
export type SChipInputMetaPlacement = 'end' | 'inline';

/** ref로 노출되는 UX 메서드 (sd-chip-input @Method sdFocus 대응) */
export interface SChipInputHandle {
  /** 입력 필드에 포커스를 이동합니다. */
  focus: () => void;
}

export interface SChipInputProps {
  /** 칩 값 목록 */
  values?: string[];
  /** 값 변경 (sdUpdate) — 전체 배열 */
  onValueChange?: (values: string[]) => void;
  /** 입력 중 문자열 변경 (sdInput) */
  onInput?: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** 칩별 에러 (배열 또는 판별 함수) */
  errors?: boolean[] | ((value: string) => boolean);
  /** 칩별 비활성 (배열 또는 판별 함수) */
  disabledChips?: boolean[] | ((value: string) => boolean);
  size?: SChipInputSize;
  disabled?: boolean;
  placeholder?: string;
  name?: string;
  rules?: Rule[];
  error?: boolean;
  /** 입력 초기화 버튼 표시 */
  useReset?: boolean;
  /** 최대 칩 개수 */
  maxCount?: number;
  /** 최대 개수·초기화 meta 위치 */
  metaPlacement?: SChipInputMetaPlacement;
  /** 중복 에러 메시지 항목명 */
  duplicateLabel?: string;
  /** 자동완성 후보 */
  suggestions?: string[];
  loadingSuggestions?: boolean;
  /** 추천 항목 (입력값 없을 때) */
  recommendedItems?: string[];
  loadingRecommendedItems?: boolean;
  /** 드롭다운 최소 너비 (숫자=px) */
  dropdownMinWidth?: number | string;
  // ── SField ──
  label?: string;
  labelWidth?: number | string;
  hint?: string;
  errorMessage?: string;
  width?: number | string;
  status?: SFieldStatus;
  icon?: SIconName;
  iconColor?: SColor;
  labelTooltip?: string;
  labelTooltipProps?: Partial<STooltipProps>;
  className?: string;
  style?: CSSProperties;
}

/** SChipInput — sd-chip-input 포팅. 칩 목록 + 입력 + 자동완성/추천 드롭다운 + 초기화. */
export const SChipInput = forwardRef<SChipInputHandle, SChipInputProps>(function SChipInput(
  {
    values = [],
    onValueChange,
    onInput,
    onFocus,
    onBlur,
    errors = [],
    disabledChips = [],
    size = 'sm',
    disabled = false,
    placeholder = '태그 입력 (Enter로 등록 / 콤마로 구분 / 띄어쓰기 불가)',
    name,
    rules,
    error,
    useReset = false,
    maxCount,
    metaPlacement = 'end',
    duplicateLabel,
    suggestions = [],
    loadingSuggestions = false,
    recommendedItems = [],
    loadingRecommendedItems = false,
    dropdownMinWidth = 200,
    label,
    labelWidth,
    hint,
    errorMessage,
    width,
    status,
    icon,
    iconColor,
    labelTooltip,
    labelTooltipProps,
    className,
    style,
  }: SChipInputProps,
  ref,
) {
  const autoId = useId();
  const resolvedName = name ?? autoId;
  const [inputValue, setInputValue] = useState('');
  const [focused, setFocused] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [suppressSuggestions, setSuppressSuggestions] = useState(false);
  const [suppressRecommendations, setSuppressRecommendations] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputShellRef = useRef<HTMLDivElement>(null);
  // 한글 등 IME 조합 상태 추적 — 브라우저별로 조합을 끝내는 Enter의 e.nativeEvent.isComposing이
  // 부정확한 경우가 있어(예: 일부 Chrome 환경) compositionstart/end로 직접 추적한다.
  const isComposingRef = useRef(false);

  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }), []);

  const chipError = (i: number): boolean => {
    if (values.indexOf(values[i]) !== i) return true; // 중복
    if (typeof errors === 'function') return errors(values[i] ?? '');
    return errors[i] ?? false;
  };
  const chipDisabled = (i: number): boolean => {
    if (disabled) return true;
    if (typeof disabledChips === 'function') return disabledChips(values[i] ?? '');
    return disabledChips[i] ?? false;
  };

  // 중복 검사 rule + 사용자 rules
  const duplicateMessage = useMemo(() => {
    const seen = new Set<string>();
    for (const v of values) {
      if (seen.has(v))
        return duplicateLabel ? `이미 입력된 ${duplicateLabel}입니다.` : '중복된 값이 존재합니다.';
      seen.add(v);
    }
    return '';
  }, [values, duplicateLabel]);
  const ruleMessage = useMemo(() => runRules(values, rules), [values, rules]);
  const resolvedErrorMessage = duplicateMessage || ruleMessage || errorMessage;
  const resolvedError = error || duplicateMessage !== '' || ruleMessage !== '';
  const resolvedStatus = status ?? (resolvedError ? 'error' : undefined);

  // SForm 등록 — 에러는 memo로 항상 반영되므로 검증 통과 여부만 보고한다 (폼 밖이면 no-op)
  useFormField({
    name,
    validate: () => runRules(values, rules) === '' && duplicateMessage === '',
    resetValidation: () => {},
  });

  const isMax = maxCount != null && values.length >= maxCount;
  const resolvedDropdownMinWidth =
    dropdownMinWidth == null
      ? undefined
      : typeof dropdownMinWidth === 'number'
        ? `${dropdownMinWidth}px`
        : dropdownMinWidth;

  const commit = (next: string[]) => onValueChange?.(next);

  const addChip = (v: string) => {
    if (maxCount != null && values.length >= maxCount) return;
    commit([...values, v]);
    setInputValue('');
    setActiveIndex(-1);
    onInput?.('');
    inputRef.current?.focus();
  };
  const removeChip = (i: number) => {
    commit(values.filter((_, idx) => idx !== i));
    // .focus()는 동기 실행 → onFocus가 suppressRecommendations=false로 리셋하므로 이후 덮어쓴다.
    inputRef.current?.focus();
    setSuppressRecommendations(true);
  };
  const reset = () => {
    const kept = values.filter((_, i) => chipDisabled(i));
    commit(kept);
    inputRef.current?.focus();
  };

  // 드롭다운 표시 로직
  const showAutocomplete =
    focused &&
    inputValue !== '' &&
    !suppressSuggestions &&
    (suggestions.length > 0 || loadingSuggestions);
  const showRecommendations =
    focused &&
    inputValue === '' &&
    !suppressRecommendations &&
    (recommendedItems.length > 0 || loadingRecommendedItems);
  const showDropdown = showAutocomplete || showRecommendations;
  // 닫힐 때 focused가 먼저 false가 되어 showAutocomplete/showRecommendations가 동시에 꺼지는데,
  // SPortal은 닫힘 애니메이션 동안 콘텐츠를 그대로 유지·재렌더하므로 마지막 모드를 기억해 유지한다.
  // (그렇지 않으면 닫히는 애니메이션 도중 추천(그리드) → 자동완성(리스트) 스타일로 잠깐 바뀌어 보인다.)
  const lastAutocompleteRef = useRef(showAutocomplete);
  useEffect(() => {
    if (showAutocomplete || showRecommendations) {
      lastAutocompleteRef.current = showAutocomplete;
    }
  }, [showAutocomplete, showRecommendations]);
  const effectiveAutocomplete = showDropdown ? showAutocomplete : lastAutocompleteRef.current;
  const dropdownAnchorRef = effectiveAutocomplete ? inputShellRef : containerRef;
  const items = effectiveAutocomplete ? suggestions : recommendedItems;
  const loading = effectiveAutocomplete
    ? loadingSuggestions && suggestions.length === 0
    : loadingRecommendedItems && recommendedItems.length === 0;
  const isFieldEmpty = inputValue === '' && values.length > 0 && !focused;
  const isFieldFilled = inputValue !== '';

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (showDropdown && items.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex(i => Math.min(i + 1, items.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex(i => Math.max(i - 1, -1));
        return;
      }
      if (e.key === 'Escape') {
        if (showAutocomplete) setSuppressSuggestions(true);
        if (showRecommendations) setSuppressRecommendations(true);
        setActiveIndex(-1);
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        const sel = items[activeIndex];
        if (!values.includes(sel)) addChip(sel);
        return;
      }
    }
    if (e.key === 'Enter' && !isComposingRef.current && inputValue.trim() !== '') {
      if (isMax) return;
      e.preventDefault();
      addChip(inputValue.trim());
    } else if (e.key === 'Backspace' && inputValue === '' && values.length > 0) {
      if (chipDisabled(values.length - 1)) return;
      e.preventDefault();
      commit(values.slice(0, -1));
    }
  };

  const showResetLink = useReset && values.length > 0;
  const showMaxCount = maxCount != null;
  const showMetaInfo = showResetLink || showMaxCount;

  const closeDropdown = () => {
    setFocused(false);
    setActiveIndex(-1);
    setSuppressSuggestions(true);
    setSuppressRecommendations(true);
  };

  return (
    <SField
      label={label}
      labelWidth={labelWidth}
      // 라벨을 칩 첫 행에 세로 중앙 정렬 — 컨트롤 박스는 기본 필드 높이(28/36)보다 큰
      // min-height(36/40) + SField 테두리 1px×2 로 렌더되므로, 그 실제 높이를 라벨에 맞춘다.
      labelHeight={`calc(var(--cmp-chipInput-${size}-minHeight) + 2px)`}
      icon={icon}
      iconColor={iconColor}
      labelTooltip={labelTooltip}
      labelTooltipProps={labelTooltipProps}
      size={size}
      hint={hint}
      error={resolvedError}
      status={resolvedStatus}
      focused={focused}
      hovered={hovered}
      errorMessage={resolvedErrorMessage}
      width={width}
      disabled={disabled}
      multiline
      className={className}
      style={style}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div
        ref={containerRef}
        className={cn(
          'relative box-border flex w-full flex-wrap content-center items-center gap-(--cmp-chipInput-chip-gap)',
          size === 'md'
            ? 'min-h-(--cmp-chipInput-md-minHeight) px-(--cmp-chipInput-md-paddingX) py-(--cmp-chipInput-md-paddingY)'
            : 'min-h-(--cmp-chipInput-sm-minHeight) px-(--cmp-chipInput-sm-paddingX) py-(--cmp-chipInput-sm-paddingY)',
        )}
        onClick={() => {
          if (!disabled) {
            setSuppressSuggestions(false);
            setSuppressRecommendations(false);
            inputRef.current?.focus();
          }
        }}
      >
        {values.map((v, i) => (
          <SChip
            key={i}
            value={v}
            error={chipError(i)}
            disabled={chipDisabled(i)}
            editable={false}
            onRemove={() => removeChip(i)}
          />
        ))}

        {!isMax && (
          <div
            ref={inputShellRef}
            className={cn(
              's-chip-input__field inline-grid shrink-0 items-center overflow-hidden',
              isFieldFilled && 's-chip-input__field--filled',
              isFieldEmpty && 's-chip-input__field--empty',
            )}
            data-value={inputValue !== '' ? inputValue : values.length === 0 ? placeholder : ''}
            style={{
              flex: '0 0 auto',
              gridTemplateColumns: 'minmax(0, max-content)',
              maxWidth: '100%',
              boxSizing: 'border-box',
              padding: 0,
              border: 'none',
              position: isFieldEmpty ? 'absolute' : undefined,
              top: isFieldEmpty ? 0 : undefined,
              left: isFieldEmpty ? 0 : undefined,
              width: isFieldEmpty ? 0 : undefined,
              minWidth: isFieldEmpty ? 0 : '1ch',
              height: isFieldEmpty ? 0 : 'var(--cmp-chip-height)',
              paddingInline: isFieldFilled ? 'var(--cmp-chip-paddingX)' : undefined,
              borderWidth: isFieldFilled ? 'var(--cmp-chip-border-width)' : undefined,
              borderStyle: isFieldFilled ? 'solid' : undefined,
              // 작성중 필드는 SChip의 editable 수정중(editing) 스타일과 동일하게 —
              // bg/보더/글자색 모두 focus 토큰을 사용한다.
              borderColor: isFieldFilled ? 'var(--cmp-chip-border-focus)' : undefined,
              borderRadius: isFieldFilled ? 'var(--cmp-chip-radius)' : 0,
              background: isFieldFilled ? 'var(--cmp-chip-bg-focus)' : 'transparent',
              overflow: 'hidden',
            }}
          >
            {/* 실제 <input>과 같은 칸에 겹쳐 렌더되는 투명 텍스트 — 타이핑한 내용만큼만 필드 폭이 늘어나게 한다. */}
            <span
              aria-hidden
              className={cn(
                'invisible col-start-1 row-start-1 whitespace-pre font-normal',
                size === 'md' ? 'text-14 leading-sd-24' : 'text-12 leading-sd-20',
              )}
            >
              {inputValue !== '' ? inputValue : values.length === 0 ? placeholder : ''}
            </span>
            <input
              ref={inputRef}
              name={resolvedName}
              size={1}
              className={cn(
                's-chip-input__input col-start-1 row-start-1 w-full border-0 bg-transparent p-0 font-normal text-(--cmp-chip-content-focus) outline-none placeholder:text-(--cmp-chipInput-text-placeholder) disabled:cursor-not-allowed',
                AUTOFILL_RESET_CLASS,
                size === 'md' ? 'text-14 leading-sd-24' : 'text-12 leading-sd-20',
              )}
              style={{
                minWidth: '1ch',
                // height는 지정하지 않고 line-height(20/24px)에 맞춰 자동 크기로 둔다.
                // var(--cmp-chip-height)(24px)로 고정하면 네이티브 <input>이 남는 세로 여백에
                // 텍스트를 아래로 밀어 넣어, 확정된 chip 텍스트보다 2px 낮게 보인다.
                margin: 0,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
              placeholder={values.length === 0 ? placeholder : ''}
              disabled={disabled}
              value={inputValue}
              onChange={e => {
                const nextValue = e.target.value;
                const wasEmpty = inputValue === '';
                const isEmpty = nextValue === '';
                if (wasEmpty !== isEmpty) {
                  setActiveIndex(-1);
                  if (isEmpty) setSuppressRecommendations(false);
                }
                setInputValue(nextValue);
                setSuppressSuggestions(false);
                onInput?.(nextValue);
              }}
              onKeyDown={onKeyDown}
              onCompositionStart={() => {
                isComposingRef.current = true;
              }}
              onCompositionEnd={() => {
                isComposingRef.current = false;
              }}
              onFocus={() => {
                setFocused(true);
                setSuppressRecommendations(false);
                onFocus?.();
              }}
              onBlur={() => {
                setFocused(false);
                setActiveIndex(-1);
                onBlur?.();
              }}
            />
          </div>
        )}

        {showMetaInfo && (
          <div
            className={cn(
              's-chip-input__meta flex items-center gap-sd-8',
              metaPlacement === 'end' && 'ml-auto',
            )}
            data-placement={metaPlacement}
            onMouseDown={e => e.preventDefault()}
          >
            <span
              className="s-chip-input__divider shrink-0"
              style={{ width: 1, height: 12, backgroundColor: 'var(--color-divider-default)' }}
            />
            {showMaxCount && (
              <span
                className={cn(
                  's-chip-input__max-count text-12',
                  isMax
                    ? 'text-(--sys-color-fg-warning)'
                    : 'text-(--cmp-chipInput-text-placeholder)',
                )}
              >
                최대 {maxCount}개
              </span>
            )}
            {showResetLink && (
              <STextLink
                className="s-chip-input__reset"
                icon={'reset' as SIconName}
                iconColor="var(--color-grey-65)"
                label="입력 초기화"
                underline
                disabled={disabled}
                onClick={reset}
                style={{ color: 'var(--color-grey-80)' } as CSSProperties}
              />
            )}
          </div>
        )}

        <SPortal
          open={showDropdown}
          anchorRef={dropdownAnchorRef}
          onClose={closeDropdown}
          onOpenAutoFocus={e => e.preventDefault()}
          onCloseAutoFocus={e => e.preventDefault()}
          maxHeight={260}
          viewportPadding={8}
          className="s-chip-input__dropdown shadow-[2px_2px_12px_2px_rgba(0,0,0,0.1)]"
          style={
            effectiveAutocomplete
              ? { minWidth: resolvedDropdownMinWidth }
              : { width: 'var(--radix-popover-trigger-width)' }
          }
        >
          <div
            role="listbox"
            className="s-chip-input__suggestions flex w-full flex-col outline-none"
            onMouseDown={e => e.preventDefault()}
          >
            {loading && (
              <div className="s-chip-input__suggestions-empty p-sd-12 text-center text-12 leading-sd-20 text-(--color-grey-65)">
                불러오는 중...
              </div>
            )}
            {!effectiveAutocomplete ? (
              <div className="flex flex-wrap gap-sd-8 px-sd-12 py-sd-6">
                {items.map((item, i) => {
                  const selected = values.includes(item);
                  const active = i === activeIndex && !selected;
                  return (
                    <div
                      key={item}
                      role="option"
                      aria-selected={selected || active}
                      className="rounded-full"
                      style={
                        active
                          ? { outline: '2px solid var(--color-border-accent)', outlineOffset: 1 }
                          : undefined
                      }
                      onMouseDown={e => {
                        e.preventDefault();
                        if (!selected) addChip(item);
                      }}
                    >
                      <SChip value={item} disabled={selected} editable={false} showRemove={false} />
                    </div>
                  );
                })}
              </div>
            ) : (
              items.map((item, i) => {
                const selected = values.includes(item);
                const active = i === activeIndex && !selected;
                return (
                  <div
                    key={item}
                    role="option"
                    aria-selected={selected || active}
                    className={cn(
                      's-chip-input__suggestion-item flex shrink-0 cursor-pointer items-center gap-sd-8 overflow-hidden text-ellipsis whitespace-nowrap px-sd-12 py-sd-4 text-12 leading-sd-20',
                      selected
                        ? 'cursor-default overflow-visible font-bold text-(--cmp-listBoxItem-content-selected)'
                        : active
                          ? 'bg-(--cmp-listBoxItem-bg-hover) font-medium text-(--cmp-listBoxItem-content-hover)'
                          : 'text-(--cmp-listBoxItem-content-default)',
                    )}
                    onMouseDown={e => {
                      e.preventDefault();
                      if (!selected) addChip(item);
                    }}
                  >
                    <span className="flex-1">{item}</span>
                    {selected && (
                      <SIcon
                        name="check"
                        size={12}
                        color="var(--cmp-listBoxItem-content-selected)"
                      />
                    )}
                  </div>
                );
              })
            )}
          </div>
        </SPortal>
      </div>
    </SField>
  );
});
